import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { getUsers, createUser, deleteUser } from '../lib/users'
import type { User } from '../lib/supabase'
import { Settings as SettingsIcon, UserPlus, Trash2, Key, Shield, Eye } from 'lucide-react'

export default function Settings() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [activeSection, setActiveSection] = useState<'accounts' | 'webhook' | 'ocr'>('accounts')
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newUsername, setNewUsername] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newDisplayName, setNewDisplayName] = useState('')
  const [createError, setCreateError] = useState('')
  const [deleteError, setDeleteError] = useState('')

  useEffect(() => {
    if (isAdmin) {
      loadUsers()
    }
  }, [isAdmin])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const data = await getUsers()
      setUsers(data || [])
    } catch (err) {
      console.error('加载用户失败:', err)
    } finally {
      setLoading(false)
    }
  }

  // 创建子账号
  const handleCreateUser = async () => {
    if (!newUsername || !newEmail) {
      setCreateError('请填写用户名和邮箱')
      return
    }
    try {
      setCreateError('')
      await createUser(newEmail, newUsername, newDisplayName || undefined)
      alert('创建成功！\n邮箱: ' + newEmail + '\n临时密码已生成，请通知用户登录')
      setShowCreateModal(false)
      setNewUsername('')
      setNewEmail('')
      setNewDisplayName('')
      await loadUsers()
    } catch (err: any) {
      setCreateError(err?.message || '创建失败')
      alert('创建失败: ' + (err?.message || '请重试'))
    }
  }

  // 删除子账号
  const handleDeleteUser = async (userId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const userToDelete = users.find(u => u.id === userId)
    if (!userToDelete) return
    
    if (!confirm(`确定要删除子账号 "${userToDelete.display_name || userToDelete.username}" 吗？`)) return
    
    try {
      setDeleteError('')
      await deleteUser(userId)
      alert('已删除子账号: ' + (userToDelete.display_name || userToDelete.username))
      await loadUsers()
    } catch (err: any) {
      setDeleteError(err?.message || '删除失败')
      alert('删除失败: ' + (err?.message || '请重试'))
    }
  }

  if (!isAdmin) {
    return (
      <div className="max-w-3xl space-y-6">
        <h1 className="text-xl font-semibold">系统设置</h1>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <Shield size={48} className="mx-auto text-yellow-500 mb-3" />
          <p className="text-gray-600">您是子账号，无权限访问此页面</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl space-y-6">
      <h1 className="text-xl font-semibold">系统设置</h1>

      {/* Section Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {[
          { id: 'accounts' as const, label: '账号管理', icon: Shield },
          { id: 'webhook' as const, label: '提醒推送', icon: SettingsIcon },
          { id: 'ocr' as const, label: 'OCR 配置', icon: Eye },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeSection === tab.id
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Accounts Section */}
      {activeSection === 'accounts' && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">账号管理</h3>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary text-sm flex items-center gap-2"
            >
              <UserPlus size={14} />
              新增子账号
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">加载中...</div>
          ) : (
            <div className="space-y-2">
              {users.map(u => (
                <div
                  key={u.id}
                  className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium text-sm">
                      {u.display_name?.[0] || u.username?.[0] || u.email?.[0]}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{u.display_name || u.username || u.email}</div>
                      <div className="text-xs text-gray-500">{u.email}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      u.role === 'admin'
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {u.role === 'admin' ? '系统管理员' : '子账号'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.role !== 'admin' && (
                      <button
                        onClick={(e) => handleDeleteUser(u.id, e)}
                        className="p-1 hover:bg-red-100 rounded text-red-500"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Webhook Section */}
      {activeSection === 'webhook' && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <h3 className="font-semibold">提醒推送配置</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL</label>
            <input type="text" placeholder="https://your-server.com/webhook" className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" defaultChecked className="accent-indigo-600" />
              启用提醒推送
            </label>
            <button className="btn btn-primary text-sm">保存配置</button>
          </div>
        </div>
      )}

      {/* OCR Section */}
      {activeSection === 'ocr' && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <h3 className="font-semibold">OCR 配置</h3>
          
          {/* MinerU Section */}
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🤖</span>
              <span className="font-medium text-green-800">MinerU 云端 OCR（推荐）</span>
            </div>
            <p className="text-xs text-green-700 mb-3">
              MinerU 支持 PDF、扫描件、复杂排版文档的高精度识别，自动提取表格和公式。
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">MinerU API Token</label>
              <input 
                type="password" 
                placeholder="sk-xxxxxxxx" 
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="mt-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">模型版本</label>
              <select className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-500 bg-white">
                <option value="pipeline">pipeline（推荐，速度快）</option>
                <option value="vlm">vlm（最精准，适合复杂排版）</option>
                <option value="MinerU-HTML">MinerU-HTML（网页转换）</option>
              </select>
            </div>
            <button className="mt-3 btn btn-primary text-sm">保存 MinerU 配置</button>
          </div>

          {/* Baidu OCR Section */}
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">📄</span>
              <span className="font-medium text-gray-800">百度 AI OCR</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
              <input type="password" placeholder="your-baidu-api-key" className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div className="mt-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Secret Key</label>
              <input type="password" placeholder="your-baidu-secret-key" className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <p className="text-xs text-gray-400 mt-2">识别语言固定为简体中文（zh-CN）</p>
            <button className="mt-3 btn btn-outline text-sm">保存百度 OCR 配置</button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
            <h3 className="font-semibold text-lg">新增子账号</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">邮箱</label>
              <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="user@example.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
              <input type="text" value={newUsername} onChange={e => setNewUsername(e.target.value)} placeholder="username" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">昵称（可选）</label>
              <input type="text" value={newDisplayName} onChange={e => setNewDisplayName(e.target.value)} placeholder="张三" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            {createError && <p className="text-sm text-red-500">{createError}</p>}
            <div className="flex justify-end gap-2">
              <button className="btn btn-outline text-sm" onClick={() => setShowCreateModal(false)}>取消</button>
              <button className="btn bg-indigo-600 text-white text-sm" onClick={handleCreateUser}>确认创建</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
